import React, { useMemo } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { GestureResponderEvent, Platform, Pressable, View } from 'react-native'

import { includes, isEmpty, noop, uniqueId } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { customContentAttributes } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/customContentAttributes'
import { AssignmentStatuses } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import {
  assignmentHasNonCompletedProviderDraft,
  getAssignmentStatusLabel,
  getCombinedAssignmentStatus,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { isRenewalCareConsent } from '@lyrahealth-inc/shared-app-logic/src/features/consent/consentUtils'
import { Assignment, AssignmentGroup } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { EpisodeContents } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  InfoPopoverPlacement,
  InfoPopoverTriggerAction,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Provider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { AssignmentIconSelector } from './AssignmentIconSelector'
import { ItemAction } from './AssignmentsList'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { Checkbox } from '../../atoms/formElements/Checkbox'
import { GenericListItem } from '../../atoms/genericListItem/GenericListItem'
import { LightbulbIcon } from '../../atoms/icons/LightbulbIcon'
import { MenuDotsIcon } from '../../atoms/icons/MenuDotsIcon'
import { InfoPopover } from '../../atoms/infoPopover/InfoPopover'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { InlineBanner } from '../../molecules/inlineBanner/InlineBanner'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

type AssignmentsListItemProps = {
  assignment?: Assignment
  assignments?: Assignment[]
  assignmentsInfo?: {
    [key: string]: {
      inlineInfoBannerMessage?: string
    }
  }
  futureEpisode?: Assignment | EpisodeContents
  assignmentTags?: AssignmentTag[]
  fullBorder?: boolean
  itemActions?: Array<ItemAction>
  itemClickHandler?: (val: any) => void
  multiProvider?: boolean
  provider?: Provider
  sessionCount?: number
  showDueDate?: boolean
  showProviderName?: boolean
  userRole: string
  isFirstItem: boolean
  isLastItem: boolean
  clientFirstName?: string
  timeSubtext?: string
  height?: string
  largeText?: boolean
  boldText?: boolean
  combineLinkedAssignments?: boolean
  simplifyStatus?: boolean
  onBulkActionPress?: (id: Assignment | EpisodeContents) => void
  bulkActionSelectedItems?: (Assignment | EpisodeContents)[]
  bulkEditMode?: boolean
  showBulkCheckbox?: (itemActions: ItemAction[], group: string) => boolean
}

type AssignmentTag = {
  type: AssignmentTagType
  text: string
}

export enum AssignmentTagType {
  default,
  success,
}

const MenuContainer = styled(View)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: spacing['24px'],
  height: spacing['24px'],
}))

const TriggerContainer = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => {
  return {
    borderRadius: Platform.OS === 'web' ? '50%' : 0,
    padding: theme.spacing['12px'],
    display: 'flex',
    position: 'absolute',
    right: '-12px',
  }
})

const RightActionOption = styled(PressableOpacity)({
  padding: '10px',
  width: '100%',
})

const RightActionOptionText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => {
  return {
    paddingLeft: theme.spacing['16px'],
  }
})

const AssignmentTagText = styled(BodyText)<{ theme: ThemeType; tagType: AssignmentTagType }>(
  ({ theme: { colors }, tagType }) => ({
    backgroundColor:
      tagType === AssignmentTagType.success
        ? colors.components.assignmentsListItem.success.background
        : colors.components.assignmentsListItem.default.background,
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: 3,
  }),
)

const InlineBannerContainer = styled.View({
  padding: '0px 24px 16px 24px',
})

const assignmentGroupLabels = defineMessages({
  lesson: {
    defaultMessage: 'Lesson',
    description: 'Title shown for the lesson activity type',
  },
  assessment: {
    defaultMessage: 'Assessment',
    description: 'Title shown for the assessment activity type',
  },
  exercise: {
    defaultMessage: 'Exercise',
    description: 'Title shown for the exercise activity type',
  },
  infosheet: {
    defaultMessage: 'Guide',
    description: 'Title shown for the guide (info sheet) activity type',
  },
})

export const AssignmentsListItem: React.FC<AssignmentsListItemProps> = ({
  assignment,
  assignments,
  assignmentsInfo,
  futureEpisode,
  assignmentTags,
  fullBorder = false,
  itemActions,
  itemClickHandler,
  multiProvider = false,
  provider,
  sessionCount,
  showDueDate = true,
  showProviderName = true,
  userRole,
  isFirstItem = false,
  isLastItem = true,
  clientFirstName,
  timeSubtext,
  height,
  largeText,
  boldText,
  combineLinkedAssignments = false,
  simplifyStatus = false,
  onBulkActionPress = noop,
  bulkActionSelectedItems = [],
  bulkEditMode,
  showBulkCheckbox,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const componentColors = colors.components.assignmentsListItem
  const { rightActionHover, rightActionPressed } = componentColors

  const assignmentName =
    assignment?.content?.name || assignment?.name || futureEpisode?.name || (futureEpisode as Assignment)?.content?.name

  const title =
    assignment?.content_meta_data?.title ||
    assignment?.content?.title ||
    assignment?.title ||
    futureEpisode?.title ||
    (futureEpisode as Assignment)?.content?.title

  const linkedAssignmentTitle =
    assignment?.content?.name && !isRenewalCareConsent({ assignment, assignments })
      ? customContentAttributes[assignment.content.name]?.linkedAssignmentTitle
      : undefined

  const group =
    assignment?.content?.group ||
    assignment?.group ||
    futureEpisode?.group ||
    (futureEpisode as Assignment)?.content?.group

  const showProviderStartedWithBadge = assignmentHasNonCompletedProviderDraft(assignment || futureEpisode)

  let onClick = () => {}
  if (itemClickHandler) {
    onClick = () => itemClickHandler(assignment)
  }

  // Filter complete list of itemActions to only those that apply to this item
  const itemActionsToShow = useMemo(() => {
    return (
      itemActions?.filter((action: ItemAction) =>
        action.shouldShow(assignment ? assignment : futureEpisode, sessionCount),
      ) || []
    )
  }, [assignment, futureEpisode, itemActions, sessionCount])

  const inactive = userRole === 'client' && assignment?.status === 'missed'

  // Disable items that cannot be bulk edited if we are in bulk edit mode
  const isDisabledBulkActionItem = useMemo(() => {
    return (
      bulkEditMode &&
      (!itemActionsToShow.some((action: ItemAction) => action.name === 'unassign') ||
        group === AssignmentGroup.ASSESSMENT)
    )
  }, [bulkEditMode, group, itemActionsToShow])

  if (
    inactive ||
    (userRole === 'provider' && group === AssignmentGroup.ASSESSMENT && assignment?.status !== 'completed')
  ) {
    onClick = () => {}
  }

  const displayStatus = getAssignmentStatusLabel({
    formatMessage,
    simplifyStatus: simplifyStatus,
    status: combineLinkedAssignments
      ? getCombinedAssignmentStatus({ assignment, assignments })
      : (assignment?.status as AssignmentStatuses),
  })

  const renderDetails = () => {
    if (isDisabledBulkActionItem) {
      return 'Cannot remove'
    }

    if (!assignment) {
      return ''
    }
    const numEntries =
      group === AssignmentGroup.EXERCISE && assignment?.response_count
        ? formatMessage(
            {
              defaultMessage: '{count, plural, one {# entry} other {# entries}}',
              description: 'Number of entries a client has submitted for an exercise',
            },
            { count: assignment.response_count },
          )
        : null
    let sessionDueDate, providerName
    let details = []

    if (userRole === 'client') {
      if (!isEmpty(provider)) {
        const appointment = provider?.appointments.find(
          (appointment) => appointment.sessionNumber === assignment.session_period,
        )
        if (multiProvider) {
          providerName = provider?.provider.display_name
        }
        if (appointment) {
          sessionDueDate = getDateFromAppointment(appointment)
        }
      }
      if (assignment.past) {
        return group === AssignmentGroup.EXERCISE ? numEntries : null
      }
      const groupLabel = assignmentGroupLabels[group as AssignmentGroup]
      if (groupLabel) details.push(formatMessage(groupLabel))
      if (showDueDate) {
        details.push(sessionDueDate)
      }

      if (group === AssignmentGroup.EXERCISE) {
        details.push(
          numEntries ||
            formatMessage({
              defaultMessage: 'Not started',
              description: 'The assignment has not yet been started',
            }),
        )
      } else {
        if (displayStatus) details.push(displayStatus)
      }

      if (showProviderName) {
        details.push(providerName)
      }
    } else if (userRole === 'provider') {
      switch (group) {
        case AssignmentGroup.EXERCISE:
          details.push(numEntries)
          break
        default:
          return displayStatus
      }
    }
    details = details.filter((detail) => !isEmpty(detail))
    if (!details.length) return ''
    return details.reduce((joined, detail) =>
      formatMessage(
        {
          defaultMessage: '{joined} • {detail}',
          description:
            'Joins two details in the subtitle of an activity list item, e.g. "Exercise • 2 entries". {joined} can already contain several details',
        },
        { joined, detail },
      ),
    )
  }

  const renderBadge = () => {
    if (!assignment && !futureEpisode?.id) {
      return null
    }
    const unreadResponseCount = assignment ? assignment?.unread_count : 0
    if (showProviderStartedWithBadge && clientFirstName) {
      return (
        <BodyText
          size={BodyTextSize.BADGE}
          text={
            <FormattedMessage
              description='Tag that shows who the provider started the exercise with'
              defaultMessage='Started with {clientFirstName}'
              values={{
                clientFirstName,
              }}
            />
          }
          style={{ textTransform: 'uppercase', color: colors.components.badge.infoPrimary.text }}
        />
      )
    }
    if (userRole === 'client') {
      if (!assignment || assignment.past) return null
      const getToDoBadge = () =>
        formatMessage({
          defaultMessage: 'To-do',
          description: 'Badge on an activity list item that the client still needs to complete',
        })
      switch (group) {
        case AssignmentGroup.ASSESSMENT:
          return !includes(['completed', 'missed'], assignment?.status) ? getToDoBadge() : null
        case AssignmentGroup.EXERCISE:
          return assignment.response_count < 1 ? getToDoBadge() : null
        default:
          return assignment?.status !== 'completed' ? getToDoBadge() : null
      }
    } else {
      if (userRole === 'provider' && unreadResponseCount > 0 && group !== AssignmentGroup.INFO_SHEET) {
        return unreadResponseCount.toString()
      }
    }
    return
  }

  const renderRightAction = () => {
    const actions = []
    if (!bulkEditMode && itemActionsToShow.length && group !== AssignmentGroup.ASSESSMENT) {
      actions.push(
        <React.Fragment key='item-actions'>
          <MenuContainer testID={tID('AssignmentsListItem-right-action')}>
            <InfoPopover
              content={
                <div>
                  {itemActionsToShow.map((action: ItemAction) => (
                    <RightActionOption
                      testID={tID(`AssignmentsListItem-dropdownAction-${action.name}`)}
                      key={action.name}
                      onPress={(e: GestureResponderEvent) => {
                        action.selectHandler(e, action.name, assignment || futureEpisode, sessionCount)
                      }}
                      hoveredBackgroundColor={rightActionHover.background}
                      pressedBackgroundColor={rightActionPressed.background}
                    >
                      <RightActionOptionText
                        testID={tID(`menuItemText-${action.name}`)}
                        text={action.text}
                        size={BodyTextSize.DEFAULT}
                        color={colors.textSecondary}
                        textAlign={'left'}
                      />
                    </RightActionOption>
                  ))}
                </div>
              }
              offset={0}
              placement={InfoPopoverPlacement.BOTTOM_RIGHT}
              shouldDisplayCloseButton={false}
              trigger={
                <TriggerContainer
                  testID={tID('AssignmentsListItem-dropdownAction-trigger')}
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Open more options',
                    description: 'Accessibility label for more assignment options',
                  })}
                >
                  <MenuDotsIcon fillColor={colors.iconDefault} />
                </TriggerContainer>
              }
              popoverTriggerAction={InfoPopoverTriggerAction.PRESS}
              closeIconColor={colors.iconDefault}
              popoverStyle={{
                container: {
                  borderRadius: '12px',
                  width: '180px',
                  paddingLeft: 0,
                  paddingRight: 0,
                },
              }}
              allowContentUnderCloseButton
              clickToClose={false}
            />
          </MenuContainer>
        </React.Fragment>,
      )
    }

    if (bulkEditMode && (assignment || futureEpisode)) {
      actions.push(
        <MenuContainer key='bulk-actions' testID={tID('AssignmentsListItem-right-action')}>
          {assignment && (
            <Checkbox
              title=''
              checked={bulkActionSelectedItems.includes(assignment)}
              onPress={() => onBulkActionPress(assignment)}
              name='AssignmentsListItem-bulk-action'
              readOnly={showBulkCheckbox && !showBulkCheckbox(itemActionsToShow, group)}
            />
          )}
          {futureEpisode && (
            <Checkbox
              title=''
              checked={bulkActionSelectedItems.includes(futureEpisode)}
              onPress={() => onBulkActionPress(futureEpisode)}
              name='AssignmentsListItem-bulk-action'
            />
          )}
        </MenuContainer>,
      )
    }
    return actions
  }

  const renderAssignmentTags = (tags: AssignmentTag[]) => {
    return tags.map((tag, idx) => {
      return (
        <AssignmentTagText
          testID={`Label-${tag.text}`}
          size={BodyTextSize.SMALL}
          key={idx}
          text={tag.text}
          color={tag.type === AssignmentTagType.success ? componentColors.success.text : componentColors.default.text}
          tagType={tag.type}
        />
      )
    })
  }

  return (
    <GenericListItem
      key={uniqueId('genericListItem')}
      onClick={bulkEditMode ? () => onBulkActionPress(assignment || futureEpisode) : onClick}
      title={combineLinkedAssignments ? (linkedAssignmentTitle ?? title) : title}
      listPressedBackgroundColor=''
      subText={timeSubtext ?? renderDetails()}
      badgeContent={renderBadge()}
      badgeShape={userRole === 'provider' ? BadgeShape.RECT : BadgeShape.ROUND}
      badgeVariant={showProviderStartedWithBadge ? BadgeVariant.INFO_PRIMARY : undefined}
      withBoldBadge={userRole === 'provider'}
      icon={group && <AssignmentIconSelector type={group} size={24} />}
      rightItems={[assignmentTags && renderAssignmentTags(assignmentTags), renderRightAction(), assignment?.badge]}
      testID={tID(`AssignmentsListItem-${assignmentName}`)}
      inactive={inactive || isDisabledBulkActionItem}
      fullBorder={fullBorder}
      isFirstItem={isFirstItem}
      isLastItem={isLastItem}
      height={height}
      largeText={largeText}
      boldText={boldText}
      disabled={isDisabledBulkActionItem}
      containerStyle={{ ...(isDisabledBulkActionItem && { backgroundColor: colors.backgroundSecondary }) }}
    >
      {assignmentsInfo && assignment && assignmentsInfo[assignment.id]?.inlineInfoBannerMessage && (
        <InlineBannerContainer>
          <InlineBanner
            text={assignmentsInfo[assignment.id].inlineInfoBannerMessage}
            testId={tID('InformedConsentBanner')}
            backgroundColor={colors.components.inlineInfoBanner.background}
            textColor={colors.components.inlineInfoBanner.text}
            icon={<LightbulbIcon size={20} fillColor={colors.components.inlineInfoBanner.infoIcon.fill} />}
            marginBottom='0px'
          />
        </InlineBannerContainer>
      )}
    </GenericListItem>
  )
}
